// src/components/sidebar/NotificationBell.tsx
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import React, { useRef } from "react";
import { TbBellRinging } from "react-icons/tb";
import CustomTooltip from "../CustomTooltip";
import { AlertBell, AlertDot } from "./Sidebar.styles";

gsap.registerPlugin(useGSAP);

interface NotificationBellProps {
  /** Quantidade em aberto. O sino só existe quando > 0. */
  count: number;
  /** Texto do tooltip, ex.: "3 tarefa(s) não realizada(s)". */
  label: string;
  ariaLabel: string;
  onActivate: () => void;
  /** Sidebar recolhida: vira um ponto no canto, sem número. */
  collapsed?: boolean;
  /** Quando a origem só informa sim/não (sem total), esconde o número. */
  showCount?: boolean;
}

/**
 * Sino de alerta usado nos itens do menu (Produtos, Pendências, Tarefas,
 * Ajuda). Entra com um "pop" e, de tempos em tempos, balança — um lembrete,
 * não um alarme piscando sem parar. O balanço gira em torno do próprio eixo
 * do sino (não desloca o item do menu ao redor dele). Some sozinho quando o
 * item para de passar `count > 0`.
 */
const NotificationBell: React.FC<NotificationBellProps> = ({
  count,
  label,
  ariaLabel,
  onActivate,
  collapsed,
  showCount = true
}) => {
  const rootRef = useRef<HTMLButtonElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.fromTo(
          rootRef.current,
          { scale: 0.35, opacity: 0, rotation: -14 },
          {
            scale: 1,
            opacity: 1,
            rotation: 0,
            duration: 0.35,
            ease: "back.out(1.8)",
            // Solta o `transform` inline ao terminar, senão ele fica
            // grudado e vence o `:active { transform: scale(0.94) }` do CSS.
            clearProps: "transform"
          }
        );

        // Toque periódico: um balanço rápido e curto, repetido com folga —
        // chama o olho de vez em quando, sem virar ruído visual constante.
        const ring = gsap.timeline({
          delay: 1,
          repeat: -1,
          repeatDelay: 4.2
        });
        ring
          .set(rootRef.current, { transformOrigin: "50% 50%" })
          .to(rootRef.current, { rotation: 12, duration: 0.09, ease: "sine.inOut" })
          .to(rootRef.current, { rotation: -9, duration: 0.11, ease: "sine.inOut" })
          .to(rootRef.current, { rotation: 5, duration: 0.09, ease: "sine.inOut" })
          .to(rootRef.current, {
            rotation: 0,
            duration: 0.14,
            ease: "sine.inOut",
            clearProps: "transform"
          });

        return () => {
          ring.kill();
        };
      });

      return () => mm.revert();
    },
    { scope: rootRef, dependencies: [count, collapsed], revertOnUpdate: true }
  );

  if (count <= 0) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onActivate();
  };

  if (collapsed) {
    return (
      <CustomTooltip title={label}>
        <AlertDot
          ref={rootRef}
          type="button"
          aria-label={ariaLabel}
          onClick={handleClick}
        />
      </CustomTooltip>
    );
  }

  return (
    <CustomTooltip title={label}>
      <AlertBell
        ref={rootRef as React.Ref<HTMLButtonElement>}
        type="button"
        aria-label={ariaLabel}
        onClick={handleClick}
      >
        <TbBellRinging />
        {showCount && <span>{count > 99 ? "99+" : count}</span>}
      </AlertBell>
    </CustomTooltip>
  );
};

export default NotificationBell;
